import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { digitalApi as api } from './api';
import { useAuth } from '../../context/AuthContext';
import { C, ACCENT } from '../../theme';
import { useToast } from '../../components/Toast';
import { Archive, Trash2 } from 'lucide-react';
import ClientCatalogueTab from './ClientCatalogueTab';
import { AudienceManager } from './DigitalPlanningEngine';

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(80,120,160,0.25)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }} onClick={onClose}>
      <div style={C.modal} onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:800, color:'#1a2a3a' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#8aa0b8', fontSize:22, cursor:'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}


export default function DigitalClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useAuth();
  const { showToast } = useToast();

  const [client, setClient] = useState(null);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name:'' });
  const [saving, setSaving] = useState(false);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState(false);
  const [deleteClientError, setDeleteClientError] = useState(null);
  const [tab, setTab] = useState('brands');
  const [audMgrOpen, setAudMgrOpen] = useState(false);
  const [audiences, setAudiences] = useState([]);
  const [audCatalogue, setAudCatalogue] = useState([]);

  async function load() {
    try {
      const [c, b] = await Promise.all([api.getClient(id), api.getBrands(parseInt(id))]);
      setClient(c); setBrands(b);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [id]);

  // Reused as-is for the Audiences tab (AudienceManager, exported from DigitalPlanningEngine.js) —
  // it expects the engine's own merged-catalogue shape (label/type/audience_comp/_db_id), not the
  // raw global Platform rows, so this is a minimal field remap, not a second catalogue merge.
  useEffect(() => {
    api.getPlatforms().then(ps => setAudCatalogue(ps.map(p => ({
      id:'db_'+p.id, _db_id:p.id, label:p.name, seller:p.seller, type:p.type||'banner', audience_comp:p.audience_comp||0.65, active:p.active!==false,
    })))).catch(()=>{});
  }, []);

  async function handleSaveBrand() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'new') await api.createBrand({ name: form.name, client_id: parseInt(id) });
      else await api.updateBrand(modal.id, { name: form.name });
      await load(); setModal(null);
    } catch(e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleDeleteBrand(e, brandId, brandName) {
    e.stopPropagation();
    if (!window.confirm(`Delete brand "${brandName}"? This cannot be undone.`)) return;
    try {
      await api.deleteBrand(brandId);
      showToast('Brand deleted', 'success');
      load();
    } catch(e) { showToast(e.message, 'danger'); }
  }

  async function handleArchiveClient() {
    try {
      const res = await api.archiveClient(parseInt(id));
      setClient(c => ({ ...c, archived: res.archived }));
      showToast(res.archived ? 'Client archived' : 'Client unarchived', 'success');
    } catch(e) { showToast(e.message, 'danger'); }
  }

  async function handleDeleteClient() {
    setDeleteClientError(null);
    try {
      await api.deleteClient(parseInt(id));
      showToast('Client deleted', 'success');
      navigate('/clients');
    } catch(e) { setDeleteClientError(e.message); }
  }

  if (loading) return <div style={{...C.page, color:'#8aa0b8'}}>Loading…</div>;
  if (!client) return <div style={{...C.page, color:'#d04040'}}>Client not found.</div>;

  return (
    <div style={C.page}>
      <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:28 }}>
        <button onClick={()=>navigate('/clients')} style={{ background:'transparent', border:`1px solid #dce8f0`, borderRadius:7, padding:'6px 14px', fontSize:12, color:'#4a6a8a', cursor:'pointer' }}>← Clients</button>
        <div>
          <h1 style={C.title}>{client.name}</h1>
          {client.industry && <div style={{ fontSize:13, color:'#8aa0b8', marginTop:2 }}>{client.industry}</div>}
        </div>
        <div style={{ flex:1 }}/>
        {isAdmin && (
          <button style={C.btnSecondary} onClick={handleArchiveClient} title={client.archived ? 'Unarchive client' : 'Archive client'}>
            <Archive size={14} style={{marginRight:5,verticalAlign:'middle'}}/>{client.archived ? 'Unarchive' : 'Archive'}
          </button>
        )}
        {isAdmin && (
          <button style={C.btnDanger} onClick={()=>{ setDeleteClientError(null); setConfirmDeleteClient(true); }}>
            <Trash2 size={14} style={{marginRight:5,verticalAlign:'middle'}}/>Delete
          </button>
        )}
        {tab==='brands' && canEdit && <button style={C.btnPrimary} onClick={()=>{ setForm({name:''}); setModal('new'); }}>+ New Brand</button>}
      </div>

      <div style={{ display:'flex', gap:2, marginBottom:24, borderBottom:'1px solid #dce8f0' }}>
        {[['brands','Brands'],['catalogue','Catalogue']].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{
            padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer',
            background:'transparent', border:'none',
            borderBottom: tab===t ? `2px solid ${ACCENT}` : '2px solid transparent',
            color: tab===t ? ACCENT : '#8aa0b8',
          }}>{l}</button>
        ))}
        <button onClick={()=>setAudMgrOpen(true)} style={{
          padding:'8px 16px', fontSize:13, fontWeight:600, cursor:'pointer',
          background:'transparent', border:'none', borderBottom:'2px solid transparent', color:'#8aa0b8',
        }}>Audiences</button>
      </div>

      {tab==='brands' && (brands.length === 0 ? (
        <div style={C.empty}>No brands yet. Create the first brand for this client.</div>
      ) : (
        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Brand</th>
              <th style={C.th}>Plans</th>
              <th style={C.th}>Added</th>
              <th style={C.th}></th>
            </tr>
          </thead>
          <tbody>
            {brands.map(b => (
              <tr key={b.id} style={C.tr} onClick={()=>navigate(`/brands/${b.id}`)}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,144,208,0.04)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                <td style={{...C.td, fontWeight:700}}>{b.name}</td>
                <td style={C.td}>
                  <span style={{ display:'inline-flex', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:`${ACCENT}12`, color:ACCENT, border:`1px solid ${ACCENT}30` }}>
                    {b.plan_count||0} {b.plan_count===1?'plan':'plans'}
                  </span>
                </td>
                <td style={{...C.td, color:'#6a8aaa'}}>
                  {b.created_at ? new Date(b.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                </td>
                <td style={{...C.td, textAlign:'right', whiteSpace:'nowrap'}} onClick={e=>e.stopPropagation()}>
                  {canEdit && <button style={C.btnSecondary} onClick={e=>{e.stopPropagation();setForm({name:b.name});setModal(b);}}>Edit</button>}
                  {isAdmin && b.plan_count===0 && <button style={C.btnDanger} onClick={e=>handleDeleteBrand(e,b.id,b.name)}>Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}

      {tab==='catalogue' && <ClientCatalogueTab clientId={parseInt(id)}/>}

      {audMgrOpen && (
        <AudienceManager audiences={audiences} setAudiences={setAudiences} catalogue={audCatalogue} clientId={parseInt(id)} onClose={()=>setAudMgrOpen(false)}/>
      )}



      {modal && (
        <Modal title={modal==='new'?'New Brand':`Edit: ${modal.name}`} onClose={()=>setModal(null)}>
          <label style={{ display:'block', marginBottom:22 }}>
            <span style={C.label}>Brand Name *</span>
            <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. Acme Brand" style={C.input} autoFocus/>
          </label>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setModal(null)}>Cancel</button>
            <button style={C.btnPrimary} onClick={handleSaveBrand} disabled={saving}>{saving?'Saving…':modal==='new'?'Create Brand':'Save'}</button>
          </div>
        </Modal>
      )}

      {confirmDeleteClient && (
        <Modal title="Delete Client" onClose={()=>setConfirmDeleteClient(false)}>
          <p style={{ margin:'0 0 16px', color:'#1a2a3a', fontSize:14 }}>
            Delete client <strong>{client.name}</strong>? This cannot be undone.
          </p>
          {deleteClientError && <div style={{...C.feedback('danger'), marginBottom:16}}>{deleteClientError}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button style={C.btnSecondary} onClick={()=>setConfirmDeleteClient(false)}>Cancel</button>
            <button style={C.btnDanger} onClick={handleDeleteClient}>Delete Client</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
